import { navigationContainer } from '@/di';
import { useBreadcrumbNavigation } from '@/shared/hooks';
import { useAppStore } from '@/shared/stores';
import { BreadcrumbItem, Breadcrumbs } from '@heroui/react';
import { IoHome, IoLogoWindows } from 'react-icons/io5';
import { Link } from 'react-router-dom';

const DEFAULT_TITLE = 'sistema de alerta temprana';
const HOME_TITLE = 'inicio';

/**
 * Componente para mostrar el título de la página actual y breadcrumbs de navegación
 * Implementa navegación por capas con posibilidad de retroceder a niveles anteriores
 *
 * Principios aplicados:
 * - SRP: Solo se encarga de mostrar la UI del breadcrumb
 * - OCP: Extensible sin modificar el código existente
 *
 * Características:
 * - Muestra título dinámico según la ruta actual
 * - Breadcrumbs navegables para volver a capas anteriores
 * - Botón de inicio para limpiar el stack de navegación
 * - Adaptación de título para página de inicio
 */
export const TitleAndTraceRoute = () => {
  const title = useAppStore((state) => state.titleMaintainer);
  const navigationState = useAppStore((state) => state.getNavigationState());
  const clearNavigationStack = useAppStore(
    (state) => state.clearNavigationStack,
  );

  const manageBreadcrumbUseCase =
    navigationContainer.getManageBreadcrumbNavigationUseCase();
  const { navigateBackToIndex } = useBreadcrumbNavigation(
    manageBreadcrumbUseCase,
  );

  const isHomeTitle = title?.toLowerCase() === HOME_TITLE;
  const titleMaintainer = isHomeTitle ? DEFAULT_TITLE : title;

  const handleBreadcrumbClick = (index: number) => {
    try {
      navigateBackToIndex(index);
    } catch (error) {
      console.warn('Error al navegar hacia atrás:', error);
    }
  };

  const handleHomeClick = () => {
    try {
      clearNavigationStack();
    } catch (error) {
      console.warn('Error al limpiar navegación:', error);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-3xl text-primary-pink/80 font-bold capitalize">
        {titleMaintainer}
      </h2>

      <Breadcrumbs
        size="lg"
        itemClasses={{
          item: 'text-primary-pink font-medium',
          separator: 'text-gradient-pink font-medium',
        }}
      >
        <BreadcrumbItem startContent={<IoHome />}>
          <Link to="/" onClick={handleHomeClick}>
            Inicio
          </Link>
        </BreadcrumbItem>

        {navigationState.breadcrumbs.map((breadcrumb, index) => (
          <BreadcrumbItem
            key={`${breadcrumb.path}-${index}`}
            startContent={<IoLogoWindows />}
            classNames={{
              item: 'data-[current=true]:text-primary-pink/60 cursor-pointer hover:text-primary-pink/80',
            }}
            className="capitalize"
          >
            <button
              type="button"
              onClick={() => handleBreadcrumbClick(index)}
              className="cursor-pointer"
            >
              {breadcrumb.title}
            </button>
          </BreadcrumbItem>
        ))}

        {!isHomeTitle &&
          title &&
          !navigationState.breadcrumbs.some(
            (b) => b.title.toLowerCase() === titleMaintainer.toLowerCase(),
          ) && (
            <BreadcrumbItem
              startContent={<IoLogoWindows />}
              classNames={{ item: 'data-[current=true]:text-primary-pink/60' }}
              className="capitalize"
            >
              {titleMaintainer}
            </BreadcrumbItem>
          )}
      </Breadcrumbs>
    </div>
  );
};
